import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import * as tg from "generic-type-guard";
import { produce } from "immer";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import { Commands, Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";

import { mergeData, validateActivity } from "../../../utilities/activityUtils";
import { fetchSagaFactory } from "../../../app/sagas/fetchSagaFactory";
import { defaultLocalizer, localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { handleIfOutdated, reloadTaskAndAntragActivity } from "../../../utilities/workflowUtils";
import { createCustomNotificationAction } from "../../../app/actions";
import { entityInstancePathFromModelPath } from "../../../utilities/pathHelpers";
import { PATH_TO_ARCHIVAL_IN_PROGRESS } from "../../../utilities/variables/documentModelPaths";
import { assertTrue } from "../../../utilities/assert";
import { extractCause, extractHttpStatusCode, isRawHttpStatusCause } from "../../../utilities/error";
import { base64ToUtf8 } from "../../../utilities/encodingHelper";
import { validateLockState } from "../../../utilities/lockProcessUtils";
import { lockProcessError } from "../../processFormEngine/store/actions";

import { ArchiveModuleFormDescriptor } from "../descriptors";
import { isArchiveModalDocumentData } from "../archiveModalTypes";
import { PAPIERFORM } from "../constants";
import { generateArchivalContent } from "../actions";

import { cancelEldoradoArchiveTask } from "./cancelEldoradoArchiveTask";

const logger = LoggerFactory.getLogger("generateEldoradoArchiveSaga");

const isEldoradoResponse = new tg.IsInterface()
	.withProperties({
		conversionId: tg.isString,
		excludedAttachments: tg.isArray(tg.isString),
		excludedMsgAttachments: tg.isArray(tg.isString)
	})
	.get();

const isConversionWarning = new tg.IsInterface()
	.withProperties({
		fileName: tg.isString,
		message: tg.isString
	})
	.get();

export function* generateEldoradoArchiveSaga(): SagaGenerator<void> {
	yield* takeLatest(generateArchivalContent.match, function* (action) {
		if (yield* call(handleIfOutdated)) {
			return;
		}

		const lockValidationResult = yield* call(validateLockState);
		if (!lockValidationResult) {
			yield* put(lockProcessError({}));
			return;
		}

		const { activityId } = action.payload;
		const activity = yield* select(ActivitySelectors.activityById(activityId));
		if (
			!ArchiveModuleFormDescriptor.isInstance(activity?.descriptor) ||
			activity.descriptor.archiveSystem !== "eldorado"
		) {
			return;
		}

		// Clear previous error metadata before validation to prevent stale error messages
		// (which may contain trailing whitespace) from causing validation failures.
		const currentData = yield* select(ActivitySelectors.data(activityId));
		if (isArchiveModalDocumentData(currentData) && currentData.document.root.metadata?.message) {
			const cleanedData = produce(currentData, draft => {
				if (draft.document.root.metadata) {
					delete draft.document.root.metadata["status"];
					delete draft.document.root.metadata["message"];
				}
			});
			yield* put(ActivityActions.setData({ activityId, data: cleanedData }));
		}

		const isValid = yield* call(validateActivity, activityId);
		if (!isValid) {
			return;
		}
		assertTrue(ArchiveModuleFormDescriptor.isInstance(activity?.descriptor));

		const data = yield* select(ActivitySelectors.data(activityId));
		assertTrue(isArchiveModalDocumentData(data));
		const { sourceActivityId, archiveLocation, instance } = activity.descriptor;
		const {
			attachments,
			initialAttachments,
			mappenNummer,
			betreff,
			absender,
			bezug,
			dokumentenArt,
			papierform,
			aktenkennzeichen,
			metadata,
			sortierungGeneratedDocuments,
			sortierungAttachments,
			sortierungInitialAttachments,
			sortierungSegments
		} = data.document.root;
		const jobUuid = metadata?.jobUuid;
		if (jobUuid) {
			yield* call(cancelEldoradoArchiveTask, { closeActivity: false, documentReference: instance });
		}
		const busyDocument = produce(data, draft => {
			if (!draft.document.root.metadata) {
				draft.document.root.metadata = {};
			}
			draft.document.root.metadata.busy = true;
			draft.document.root.metadata.cancelDisabled = true;
		});
		yield* put(ActivityActions.setData({ activityId, data: busyDocument }));
		yield* call(
			fetchSagaFactory(
				{
					relativeUrl: `/eldorado/startArchiving`,
					method: "POST",
					body: JSON.stringify({
						documentReference: instance,
						pathToArchiveGroup: archiveLocation,
						attachmentIds: attachments?.map(att => att.value) ?? [],
						initialAttachmentIds: initialAttachments?.map(att => att.value) ?? [],
						aktenkennzeichen,
						mappenNummer,
						betreff,
						absender,
						bezug,
						dokumentenArt,
						papierform: papierform ?? PAPIERFORM.NEIN,
						initialAttachmentsSorting: sortierungInitialAttachments?.map(item => item.id) ?? undefined,
						additionalAttachmentsSorting: sortierungAttachments?.map(item => item.id) ?? undefined,
						generatedDocumentsSorting: sortierungGeneratedDocuments?.map(item => item.id) ?? undefined,
						customSegmentSorting: sortierungSegments?.map(item => item.id) ?? undefined
					})
				},
				function* handleResponse(response) {
					const result = yield* call(response.json.bind(response));

					if (!isEldoradoResponse(result)) {
						throw new Error("Response is not a valid eldorado response.");
					}
					yield* put(
						FormEngineActions.event({
							activityId: sourceActivityId,
							engineEvent: Events.valueChange({
								path: entityInstancePathFromModelPath(PATH_TO_ARCHIVAL_IN_PROGRESS),
								value: result.conversionId
							})
						})
					);

					if (result.excludedAttachments.length > 0 || result.excludedMsgAttachments.length > 0) {
						const combinedMessage = buildExcludedMessage(
							result.excludedAttachments,
							result.excludedMsgAttachments
						);
						const excludedAttachmentNames =
							result.excludedAttachments.length > 0 ? result.excludedAttachments.join(", ") : undefined;
						const excludedMsgNames =
							result.excludedMsgAttachments.length > 0
								? result.excludedMsgAttachments.join(", ")
								: undefined;
						const updatedDocument = produce(busyDocument, draft => {
							if (!draft.document.root.metadata) {
								draft.document.root.metadata = {};
							}
							draft.document.root.metadata.jobUuid = result.conversionId;
							draft.document.root.metadata.status = "warning";
							draft.document.root.metadata.message = combinedMessage;
							draft.document.root.metadata.cancelDisabled = false;
							if (excludedAttachmentNames) {
								draft.document.root.metadata.excludedAttachments = excludedAttachmentNames;
							}
							if (excludedMsgNames) {
								draft.document.root.metadata.excludedMsgAttachments = excludedMsgNames;
							}
						});
						yield* put(ActivityActions.setData({ activityId, data: updatedDocument }));
					} else {
						const updatedDocument = produce(busyDocument, draft => {
							if (!draft.document.root.metadata) {
								draft.document.root.metadata = {};
							}
							draft.document.root.metadata.jobUuid = result.conversionId;
							draft.document.root.metadata.cancelDisabled = false;
						});
						yield* put(ActivityActions.setData({ activityId, data: updatedDocument }));
					}

					yield* put(ActivityActions.setDirty({ activityId, dirty: true }));
					const pollSucceeded = yield* call(pollZipArchive, result.conversionId, activityId);
					if (!pollSucceeded) {
						return;
					}
					// After polling, re-apply excluded attachments warnings if they were set — the poll clears status/message
					const hasExcluded =
						result.excludedAttachments.length > 0 || result.excludedMsgAttachments.length > 0;
					if (hasExcluded) {
						const dataAfterPoll = yield* select(ActivitySelectors.data(activityId));
						if (isArchiveModalDocumentData(dataAfterPoll)) {
							const combinedMessage = buildExcludedMessage(
								result.excludedAttachments,
								result.excludedMsgAttachments
							);
							const warningDocument = produce(dataAfterPoll, draft => {
								if (!draft.document.root.metadata) {
									draft.document.root.metadata = {};
								}
								draft.document.root.metadata.status = "warning";
								draft.document.root.metadata.message = combinedMessage;
							});
							yield* put(ActivityActions.setData({ activityId, data: warningDocument }));
						}
					}
					yield* put(FormEngineActions.command({ activityId, engineEvent: Commands.setReadonly(true) }));
					yield* call(reloadTaskAndAntragActivity);
				},
				function* handleError(error: unknown) {
					logger.error(error);

					const cause = extractCause(error);
					const statusCode = extractHttpStatusCode(error);
					const isRawHttpStatus = isRawHttpStatusCause(cause);
					let localizableKey;
					if (statusCode === 401) {
						localizableKey = RESOURCE_KEYS.features.notification.error.eldorado.authenticationError;
					} else if (statusCode === 409) {
						localizableKey = RESOURCE_KEYS.features.notification.error.eldorado.conflictError;
					} else if (statusCode === 423) {
						localizableKey = RESOURCE_KEYS.features.notification.error.eldorado.lockedError;
					} else {
						localizableKey = undefined;
					}

					const localizableMessage = localizableKey
						? localizableFromLocaleResources(localizableKey)
						: cause && !isRawHttpStatus
							? localizableFromLocaleResources(
									RESOURCE_KEYS.features.notification.error.eldorado.generatingErrorWithCause,
									{ cause: { type: "plain", value: cause } }
								)
							: localizableFromLocaleResources(
									RESOURCE_KEYS.features.notification.error.eldorado.generatingError
								);

					yield* put(
						createCustomNotificationAction({
							title: { key: RESOURCE_KEYS.features.notification.title.error },
							message: localizableMessage,
							severity: "error",
							activityId,
							group: `eldoradoArchiving:${activityId}`
						})
					);

					const errorMessage = (defaultLocalizer(localizableMessage) ?? "").trim();
					const currentData = yield* select(ActivitySelectors.data(activityId));
					if (isArchiveModalDocumentData(currentData)) {
						const resetDocument = produce(currentData, draft => {
							if (!draft.document.root.metadata) {
								draft.document.root.metadata = {};
							}
							draft.document.root.metadata.status = "error";
							draft.document.root.metadata.message = errorMessage;
							draft.document.root.metadata.busy = false;
							draft.document.root.metadata.cancelDisabled = false;
						});
						yield* put(ActivityActions.setData({ activityId, data: resetDocument }));
					} else {
						yield* call(mergeData, activityId, {
							status: "error",
							message: errorMessage,
							busy: false,
							cancelDisabled: false
						});
					}
					yield* put(FormEngineActions.command({ activityId, engineEvent: Commands.setReadonly(false) }));
				}
			)
		);
	});
}

function buildExcludedMessage(excludedAttachments: string[], excludedMsgAttachments: string[]): string {
	const attachmentsMsg =
		excludedAttachments.length > 0
			? (
					defaultLocalizer(
						localizableFromLocaleResources(
							RESOURCE_KEYS.features.notification.warning.eldorado.excludedAttachments
						)
					) ?? ""
				).replace("$excludedFiles$", excludedAttachments.join(", "))
			: undefined;
	const msgAttachmentsMsg =
		excludedMsgAttachments.length > 0
			? (
					defaultLocalizer(
						localizableFromLocaleResources(
							RESOURCE_KEYS.features.notification.warning.eldorado.excludedMsgAttachments
						)
					) ?? ""
				).replace("$excludedFiles$", excludedMsgAttachments.join(", "))
			: undefined;
	return [attachmentsMsg, msgAttachmentsMsg].filter(Boolean).join(" ").trim();
}

export function* pollZipArchive(conversionJobUuid: string, activityId: string): SagaGenerator<boolean> {
	let succeeded = true;
	yield* call(
		fetchSagaFactory(
			{
				relativeUrl: `/eldorado/getArchivingPreview?conversionId=${conversionJobUuid}`,
				method: "POST",
				customHeaders: [["Accept", "application/x-zip-compressed, application/json"]]
			},
			function* handleResponse(response) {
				if (response.status === 202) {
					return 5000; // Poll again after 5000 ms
				}

				const conversionWarnings = parseConversionWarnings(response.headers.get("X-Doc-Conversion-Warnings"));
				const blob = yield* call(response.blob.bind(response));
				const data = yield* select(ActivitySelectors.data(activityId));
				assertTrue(isArchiveModalDocumentData(data));
				if (conversionWarnings.length > 0) {
					yield* put(
						createCustomNotificationAction({
							title: { key: RESOURCE_KEYS.features.notification.title.warning },
							message: localizableFromLocaleResources(
								RESOURCE_KEYS.features.notification.warning.eldorado.docConversion,
								{
									conversionWarnings: {
										type: "plain",
										value: conversionWarnings
											.map(warning => `${warning.fileName}: ${warning.message}`)
											.join("; ")
									}
								}
							),
							severity: "warning",
							activityId,
							group: `eldoradoArchiving:${activityId}`
						})
					);
				}
				const updatedDocument = produce(data, draft => {
					if (!draft.document.root.metadata) {
						draft.document.root.metadata = {};
					}
					draft.document.root.metadata.archivePreviewUrl = URL.createObjectURL(blob);
					if (conversionWarnings.length > 0) {
						draft.document.root.metadata.status = "warning";
						draft.document.root.metadata.message = conversionWarnings
							.map(warning => `${warning.fileName}: ${warning.message}`)
							.join(" ");
					} else {
						delete draft.document.root.metadata["status"];
						delete draft.document.root.metadata["message"];
					}
					draft.document.root.metadata.busy = false;
				});
				yield* put(ActivityActions.setData({ activityId, data: updatedDocument }));
				return undefined; // Done
			},
			function* handleError(error: unknown) {
				logger.error(error);
				succeeded = false;
				const activities = yield* select(ActivitySelectors.activities());
				if (!(activityId in activities)) {
					return;
				}

				const cause = extractCause(error);
				const statusCode = extractHttpStatusCode(error);
				// Only show cause if it contains meaningful backend content (not just a raw HTTP status line)
				const isRawHttpStatus = isRawHttpStatusCause(cause);
				let localizableKey;
				if (statusCode === 401) {
					localizableKey = RESOURCE_KEYS.features.notification.error.eldorado.authenticationError;
				} else {
					localizableKey = undefined;
				}

				const localizableMessage = localizableKey
					? localizableFromLocaleResources(localizableKey)
					: cause && !isRawHttpStatus
						? localizableFromLocaleResources(
								RESOURCE_KEYS.features.notification.error.eldorado.generatingErrorWithCause,
								{ cause: { type: "plain", value: cause } }
							)
						: localizableFromLocaleResources(
								RESOURCE_KEYS.features.notification.error.eldorado.generatingError
							);

				const errorMessage = (defaultLocalizer(localizableMessage) ?? "").trim();

				yield* put(
					createCustomNotificationAction({
						title: { key: RESOURCE_KEYS.features.notification.title.error },
						message: localizableMessage,
						severity: "error",
						activityId,
						group: `eldoradoArchiving:${activityId}`
					})
				);

				const data = yield* select(ActivitySelectors.data(activityId));
				assertTrue(isArchiveModalDocumentData(data));
				const updatedDocument = produce(data, draft => {
					if (!draft.document.root.metadata) {
						draft.document.root.metadata = {};
					}
					draft.document.root.metadata.status = "error";
					draft.document.root.metadata.message = errorMessage;
					draft.document.root.metadata.busy = false;
					draft.document.root.metadata.cancelDisabled = false;
				});
				yield* put(ActivityActions.setData({ activityId, data: updatedDocument }));
				yield* put(FormEngineActions.command({ activityId, engineEvent: Commands.setReadonly(false) }));
			}
		)
	);
	return succeeded;
}

function parseConversionWarnings(header: string | null): tg.GuardedType<typeof isConversionWarning>[] {
	if (!header) {
		return [];
	}
	try {
		const warnings: unknown = JSON.parse(base64ToUtf8(header));
		return tg.isArray(isConversionWarning)(warnings) ? warnings : [];
	} catch (error) {
		logger.warn("Could not parse DocConversion warnings from archive response header.", error);
		return [];
	}
}
